import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import { productService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { formatCurrency, formatDate as formatLocaleDate, formatNumber } from '../utils/i18n';
import { getImageUrl } from '../utils/imageUtils';

function RecentlyViewedNew() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const theme = getThemeVars(ACTIVE_THEME);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    let isMounted = true;
    const loadRecentlyViewed = async () => {
      setLoading(true);
      try {
        const response = await productService.getRecentlyViewed(30);
        if (isMounted) setProducts(response.data || []);
      } catch {
        if (isMounted) setProducts([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadRecentlyViewed();
    return () => { isMounted = false; };
  }, [user, navigate]);

  const formatViewedDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.ceil(Math.abs(now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return t('Hoy');
    if (diffDays === 1) return t('Ayer');
    if (diffDays < 7) return t('Hace {{days}} días', { days: diffDays });
    if (diffDays < 30) return t('Hace {{weeks}} semanas', { weeks: Math.floor(diffDays / 7) });
    return formatLocaleDate(dateString, { options: { year: 'numeric', month: 'short', day: 'numeric' } });
  };

  const getStatusInfo = (status) => {
    const map = {
      AVAILABLE: { label: t('Disponible'), cls: 'bg-primary/10 text-primary' },
      RESERVED: { label: t('Reservado'), cls: 'bg-secondary/10 text-secondary' },
      SOLD: { label: t('Vendido'), cls: 'bg-error-dim/10 text-error-dim' },
    };
    return map[status] || { label: status, cls: 'bg-surface-container text-on-surface-variant' };
  };

  const getConditionLabel = (condition) => {
    const labels = {
      NEW: t('Nuevo'), LIKE_NEW: t('Como nuevo'), GOOD: t('Bueno'),
      FAIR: t('Regular'), POOR: t('Malo'), USED: t('Usado'),
    };
    return labels[condition] || condition;
  };

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="/" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-center gap-4 mb-6 tablet-sm:mb-8">
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full hover:bg-surface-container flex items-center justify-center transition-colors shrink-0">
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h1 className="text-2xl mobile-sm:text-3xl font-black text-on-surface tracking-tight min-w-0">
            {t('Productos vistos recientemente')}
          </h1>
        </div>
        {products.length > 0 && (
          <button
            onClick={() => setProducts([])}
            className="w-full mobile-sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded-xl border-2 border-outline-variant/30 text-on-surface-variant font-medium hover:bg-surface-container-low transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">clear_all</span>
            {t('Limpiar historial')}
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="grid grid-cols-2 mobile-md:grid-cols-3 tablet-md:grid-cols-4 gap-3 mobile-sm:gap-4 tablet-sm:gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-surface-container rounded-xl shadow-sm overflow-hidden">
              <div className="skeleton h-48 w-full" />
              <div className="p-4 space-y-2">
                <div className="skeleton h-5 w-3/4 rounded" />
                <div className="skeleton h-6 w-1/2 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty */}
      {!loading && products.length === 0 && (
        <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-center gap-3 p-4 rounded-lg bg-secondary-container text-on-secondary-container">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined">info</span>
            <span>{t('No viste productos recientemente.')}</span>
          </div>
          <button onClick={() => navigate('/')} className="w-full mobile-sm:w-auto mobile-sm:ml-auto px-4 py-1.5 rounded-lg border-2 border-on-secondary-container/30 text-on-secondary-container text-sm font-medium hover:bg-on-secondary-container/10 transition-colors">
            {t('Explorar productos')}
          </button>
        </div>
      )}

      {/* Grid */}
      {!loading && products.length > 0 && (
        <div className="grid grid-cols-2 mobile-md:grid-cols-3 tablet-md:grid-cols-4 gap-3 mobile-sm:gap-4 tablet-sm:gap-6">
          {products.map((product) => {
            const statusInfo = getStatusInfo(product.status);
            return (
              <div
                key={product.id}
                onClick={() => navigate(`/products/${product.id}`)}
                className="bg-surface-container rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-lg transition-shadow flex flex-col"
              >
                <div className="relative">
                  <img
                    src={product.imageUrl || getImageUrl(product.imageId) || '/placeholder-product.jpg'}
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="text-on-surface font-medium truncate">{product.name}</h3>
                  <p className="text-lg font-bold text-primary mt-1">{formatCurrency(product.price, { currency: 'EUR' })}</p>

                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.cls}`}>
                      {statusInfo.label}
                    </span>
                    {product.condition && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border border-outline-variant/30 text-on-surface-variant">
                        {getConditionLabel(product.condition)}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col mobile-sm:flex-row mobile-sm:items-center gap-2 mobile-sm:gap-4 mt-3 text-xs text-on-surface-variant">
                    <div className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-[14px]">schedule</span>
                      {formatViewedDate(product.viewedAt || product.updatedAt || product.createdAt)}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-[14px]">visibility</span>
                      {t('{{count}} vistas', { count: formatNumber(product.viewCount || 0) })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
        </div>
      </main>
    </div>
  );
}

export default RecentlyViewedNew;
